/**
 * Pure helpers for `NumberBaseTool`. Tests live in
 * `tests/web/numberBaseTool.test.ts`.
 *
 * Backed by `BigInt` so values larger than `Number.MAX_SAFE_INTEGER`
 * round-trip without quietly losing precision (a recurring trap in
 * other tools that use `parseInt` everywhere).
 */

export type BaseValue = bigint;

export interface ParseResult {
  ok: boolean;
  value?: BaseValue;
  /** The base actually used after auto-prefix detection. */
  detected?: number;
  error?: string;
  /** True when the parser stripped a `0x` / `0b` / `0o` prefix. */
  prefixStripped?: boolean;
}

const MIN_BASE = 2;
const MAX_BASE = 36;

/**
 * Parse `input` as an integer, optionally honouring a literal prefix.
 *
 * - `autoPrefix` defaults to `true`: `0x` → 16, `0b` → 2, `0o` → 8.
 *   When present the prefix wins regardless of `fromBase`.
 * - Allows a leading `-` (so we can show two's-complement separately
 *   from raw negative magnitude in the UI).
 * - Allows ASCII letters case-insensitively up to base 36.
 */
export function parseNumber(
  input: string,
  fromBase: number,
  autoPrefix: boolean = true,
): ParseResult {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, error: '输入为空' };

  if (fromBase < MIN_BASE || fromBase > MAX_BASE || !Number.isInteger(fromBase)) {
    return { ok: false, error: `进制必须是 ${MIN_BASE}–${MAX_BASE} 的整数` };
  }

  let body = trimmed;
  let negative = false;
  if (body.startsWith('-')) {
    negative = true;
    body = body.slice(1);
  } else if (body.startsWith('+')) {
    body = body.slice(1);
  }
  if (body.length === 0) return { ok: false, error: '只输入了符号' };

  let base = fromBase;
  let prefixStripped = false;
  if (autoPrefix) {
    const lower = body.slice(0, 2).toLowerCase();
    if (lower === '0x') {
      base = 16;
      body = body.slice(2);
      prefixStripped = true;
    } else if (lower === '0b') {
      base = 2;
      body = body.slice(2);
      prefixStripped = true;
    } else if (lower === '0o') {
      base = 8;
      body = body.slice(2);
      prefixStripped = true;
    }
  }
  if (body.length === 0) return { ok: false, error: '前缀后没有数字' };

  // Validate that every char is legal for `base` BEFORE handing it to
  // BigInt — BigInt's own constructor throws but with a less actionable
  // message ("SyntaxError: Cannot convert ..."). The Chinese error
  // here matches the UI's "失败" toast tone.
  if (!isValidDigits(body, base)) {
    return { ok: false, error: `存在非 base ${base} 字符` };
  }

  // BigInt accepts `0x`/`0b`/`0o` literals natively; for arbitrary
  // bases we accumulate manually.
  let value: BaseValue = 0n;
  const baseBig = BigInt(base);
  const lowerBody = body.toLowerCase();
  for (let i = 0; i < lowerBody.length; i++) {
    const digit = digitValue(lowerBody.charCodeAt(i));
    value = value * baseBig + BigInt(digit);
  }
  if (negative) value = -value;

  return { ok: true, value, detected: base, prefixStripped };
}

/** Render `value` as a base-`base` string. Negative values print with a leading `-`. */
export function toBase(
  value: BaseValue,
  base: number,
  opts: { uppercase?: boolean } = {},
): string {
  if (base < MIN_BASE || base > MAX_BASE || !Number.isInteger(base)) {
    throw new Error(`进制必须是 ${MIN_BASE}–${MAX_BASE} 的整数`);
  }
  const out = value.toString(base);
  return opts.uppercase ? out.toUpperCase() : out;
}

/**
 * Render `value` as a 32-bit two's-complement bit string.
 * Used by the "位运算可视化" panel; only sensible for values that fit
 * in 32 bits, otherwise we surface a hint instead of a misleading row.
 */
export function toTwosComplement32(value: BaseValue): string | null {
  // Range: [-2^31, 2^32 - 1] — accept either a signed 32-bit value
  // (negative two's-complement) or an unsigned 32-bit value.
  const MIN = -(1n << 31n);
  const MAX = (1n << 32n) - 1n;
  if (value < MIN || value > MAX) return null;
  const wrapped = value < 0n ? value + (1n << 32n) : value;
  return wrapped.toString(2).padStart(32, '0');
}

function isValidDigits(body: string, base: number): boolean {
  for (let i = 0; i < body.length; i++) {
    const v = digitValue(body.charCodeAt(i));
    if (v < 0 || v >= base) return false;
  }
  return true;
}

function digitValue(code: number): number {
  // 0..9
  if (code >= 48 && code <= 57) return code - 48;
  // a..z
  if (code >= 97 && code <= 122) return code - 97 + 10;
  // A..Z
  if (code >= 65 && code <= 90) return code - 65 + 10;
  return -1;
}

export const NUMBER_BASE_LIMITS = { MIN_BASE, MAX_BASE } as const;
